import React from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { motion } from 'motion/react';
import { ArrowLeft, Share2, Printer, Bookmark } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { educationModules } from '../constants/educationData';

export default function EducationDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const module = educationModules.find(m => m.slug === slug);

  if (!module) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center p-6 text-center">
        <h2 className="text-2xl font-bold mb-4">Modul tidak ditemukan</h2>
        <Button onClick={() => navigate('/education')}>Kembali ke Edukasi</Button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header Overlay */}
      <div className="bg-navy pt-24 pb-32 px-6 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-gold/10 rounded-full -mr-48 -mt-48 blur-3xl"></div>
        <div className="absolute bottom-0 left-0 w-64 h-64 bg-primary/10 rounded-full -ml-32 -mb-32 blur-3xl"></div>
        
        <div className="max-w-4xl mx-auto relative z-10">
          <Link 
            to="/education" 
            className="inline-flex items-center gap-2 text-white/60 hover:text-gold transition-colors mb-8 text-sm font-black uppercase tracking-widest"
          >
            <ArrowLeft className="h-4 w-4" /> Kembali
          </Link>
          
          <div className="flex items-center gap-3 mb-6">
            <span className="px-3 py-1 bg-gold/20 text-gold rounded-full text-[10px] font-black uppercase tracking-widest border border-gold/30">
              {module.category}
            </span>
          </div>
          
          <h1 className="text-3xl md:text-5xl font-black text-white mb-6 leading-tight tracking-tight">
            {module.title}
          </h1>
          
          <div className="flex flex-wrap items-center gap-6 text-white/50 text-xs">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-slate-800 flex items-center justify-center text-gold">
                <module.icon className="h-4 w-4" />
              </div>
              <span className="font-bold text-white/70">Pusat Edukasi Jasa Konstruksi</span>
            </div>
            <span className="w-1 h-1 rounded-full bg-white/20"></span>
            <span>Estimasi baca: 5 Menit</span>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-6 -mt-16 relative z-20 pb-20">
        <div className="bg-card border border-border rounded-[2.5rem] p-8 md:p-12 shadow-2xl">
          {/* Action Bar */}
          <div className="flex items-center justify-end gap-3 mb-10 pb-6 border-b border-border">
            <button className="p-2.5 rounded-xl hover:bg-muted text-muted-foreground transition-colors" title="Simpan">
              <Bookmark className="h-5 w-5" />
            </button>
            <button className="p-2.5 rounded-xl hover:bg-muted text-muted-foreground transition-colors" title="Bagikan">
              <Share2 className="h-5 w-5" />
            </button>
            <button 
              onClick={() => window.print()} 
              className="p-2.5 rounded-xl hover:bg-muted text-muted-foreground transition-colors" 
              title="Cetak"
            >
              <Printer className="h-5 w-5" />
            </button>
          </div>

          <div 
            className="prose prose-slate dark:prose-invert max-w-none 
              prose-headings:font-black prose-headings:tracking-tight prose-headings:text-navy dark:prose-headings:text-white
              prose-h2:text-2xl prose-h2:mb-6 prose-h2:mt-10
              prose-h3:text-xl prose-h3:mb-4 prose-h3:mt-8
              prose-p:text-muted-foreground prose-p:leading-relaxed prose-p:text-lg
              prose-ul:list-disc prose-ol:list-decimal
              prose-li:text-muted-foreground prose-li:mb-2
              prose-strong:text-foreground prose-strong:font-bold"
            dangerouslySetInnerHTML={{ __html: module.content }}
          />

          {/* Footer of Content */}
          <div className="mt-16 pt-8 border-t border-border flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="text-sm text-muted-foreground italic">
              Sumber: Standar Kompetensi Kerja Nasional Indonesia (SKKNI) & Regulasi PUPR
            </div>
            <div className="flex items-center gap-3">
              <span className="text-xs font-black text-muted-foreground uppercase tracking-widest">Bermanfaat?</span>
              <div className="flex gap-2">
                <button className="px-4 py-2 rounded-lg bg-emerald-500/10 text-emerald-500 text-xs font-bold hover:bg-emerald-500 hover:text-white transition-all">Ya</button>
                <button className="px-4 py-2 rounded-lg bg-red-500/10 text-red-500 text-xs font-bold hover:bg-red-500 hover:text-white transition-all">Tidak</button>
              </div>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <div className="mt-12 flex justify-center">
          <Link 
            to="/education"
            className="inline-flex items-center gap-3 px-8 py-4 bg-navy text-white font-black uppercase tracking-widest rounded-2xl hover:bg-navy-light transition-all shadow-xl hover:scale-105 active:scale-95"
          >
            Lihat Modul Lainnya <ArrowLeft className="h-4 w-4 rotate-180" />
          </Link>
        </div>
      </div>
    </div>
  );
}
